/* ==========================================================================
   Eden Hours - shared enlarged image viewer.

   Both product page systems already show a large main image with a
   thumbnail rail. What they did not share was the enlarged view: the theme
   opened a dialog that stacked every image at roughly twice the screen
   height, and the Shogun flagship pages had no enlarged view at all.

   This file gives both of them the same viewer: one image at a time, always
   inside the screen, with chevrons, a counter, a thumbnail strip, a close
   control, arrow keys and swipe.

   It reads the images out of whichever gallery is on the page at the moment
   of the click, so variant filtering and media order are whatever the page
   already decided. It adds nothing to the page until the customer asks to
   enlarge something, and it never removes or reorders product media.
   ========================================================================== */
(function () {
  'use strict';

  var SVG_CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var SVG_PREV = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  var SVG_NEXT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>';

  var lb = null;
  var els = {};
  var items = [];
  var index = 0;
  var lastFocus = null;

  /* ---- media collection -------------------------------------------------
     One source of truth per page: whatever the live gallery is showing.
     Shopify CDN URLs carry their size in the query string, so the width is
     swapped for a large one and the same image is reused for the thumb.   */

  function atWidth(src, w) {
    if (!src) return src;
    try {
      var u = new URL(src, location.href);
      if (u.searchParams.has('width')) {
        u.searchParams.set('width', String(w));
        return u.href;
      }
      return src;
    } catch (e) {
      return src;
    }
  }

  function bestSrc(img) {
    return img.currentSrc || img.src || '';
  }

  function keyOf(src) {
    try {
      var u = new URL(src, location.href);
      return u.origin + u.pathname;
    } catch (e) {
      return src;
    }
  }

  function visible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    return r.width > 2 && r.height > 2;
  }

  function collect() {
    var out = [];
    var seen = {};

    function push(img) {
      var src = bestSrc(img);
      if (!src) return;
      var k = keyOf(src);
      if (seen[k]) return;
      seen[k] = 1;
      out.push({
        full: atWidth(src, 1800),
        thumb: atWidth(src, 160),
        alt: img.getAttribute('alt') || ''
      });
    }

    // the theme's gallery
    var gallery = document.querySelector('media-gallery');
    if (gallery) {
      var slides = gallery.querySelectorAll('slideshow-slide img, .product-media img');
      for (var i = 0; i < slides.length; i++) push(slides[i]);
    }

    // Shogun's gallery: the main image first, then the thumbnail rail, which
    // is where the rest of the media lives on the flagship pages
    if (!out.length) {
      var main = document.querySelector('.shg-product-image-wrapper img, .shg-gallery-scroll-container img');
      if (main && visible(main)) push(main);
      var thumbs = document.querySelectorAll('.shg-gallery-thumb-image');
      for (var j = 0; j < thumbs.length; j++) push(thumbs[j]);
    }

    return out;
  }

  /* ---- the viewer ------------------------------------------------------- */

  function build() {
    if (lb) return lb;

    lb = document.createElement('div');
    lb.className = 'eh-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Product images');
    lb.setAttribute('data-open', 'false');

    lb.innerHTML =
      '<div class="eh-lb__bar">' +
        '<p class="eh-lb__count"></p>' +
        '<button type="button" class="eh-lb__close" aria-label="Close">' + SVG_CLOSE + '</button>' +
      '</div>' +
      '<div class="eh-lb__stage">' +
        '<button type="button" class="eh-lb__nav eh-lb__nav--prev" aria-label="Previous image">' + SVG_PREV + '</button>' +
        '<div class="eh-lb__frame"><img class="eh-lb__img" alt=""></div>' +
        '<button type="button" class="eh-lb__nav eh-lb__nav--next" aria-label="Next image">' + SVG_NEXT + '</button>' +
      '</div>' +
      '<div class="eh-lb__rail" aria-label="Choose an image"></div>';

    els.count = lb.querySelector('.eh-lb__count');
    els.close = lb.querySelector('.eh-lb__close');
    els.prev = lb.querySelector('.eh-lb__nav--prev');
    els.next = lb.querySelector('.eh-lb__nav--next');
    els.img = lb.querySelector('.eh-lb__img');
    els.rail = lb.querySelector('.eh-lb__rail');
    els.stage = lb.querySelector('.eh-lb__stage');
    els.frame = lb.querySelector('.eh-lb__frame');

    els.close.addEventListener('click', close);
    els.prev.addEventListener('click', function () { step(-1); });
    els.next.addEventListener('click', function () { step(1); });

    // clicking the ground closes; clicking the image itself does not
    lb.addEventListener('click', function (e) {
      if (e.target === lb || e.target === els.stage || e.target === els.frame) close();
    });

    els.img.addEventListener('load', function () { lb.setAttribute('data-loading', 'false'); });

    // swipe
    var x0 = null, y0 = null;
    els.stage.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    }, { passive: true });

    els.stage.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var t = e.changedTouches && e.changedTouches[0];
      if (t) {
        var dx = t.clientX - x0;
        var dy = t.clientY - y0;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
      }
      x0 = null;
      y0 = null;
    }, { passive: true });

    document.body.appendChild(lb);
    return lb;
  }

  function renderRail() {
    els.rail.innerHTML = '';
    if (items.length < 2) return;
    for (var i = 0; i < items.length; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'eh-lb__thumb';
      b.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + items.length);
      b.setAttribute('data-i', String(i));
      b.innerHTML = '<img src="' + items[i].thumb + '" alt="" loading="lazy">';
      b.addEventListener('click', function () { show(parseInt(this.getAttribute('data-i'), 10)); });
      els.rail.appendChild(b);
    }
  }

  function show(i) {
    if (!items.length) return;
    index = (i + items.length) % items.length;
    var it = items[index];
    lb.setAttribute('data-loading', 'true');
    els.img.src = it.full;
    els.img.alt = it.alt;
    els.count.textContent = (index + 1) + ' / ' + items.length;

    var single = items.length < 2;
    els.prev.hidden = single;
    els.next.hidden = single;

    var thumbs = els.rail.children;
    for (var k = 0; k < thumbs.length; k++) {
      thumbs[k].setAttribute('aria-current', k === index ? 'true' : 'false');
    }
    var active = thumbs[index];
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
  }

  function step(d) { show(index + d); }

  function open(startSrc) {
    var found = collect();
    if (!found.length) return false;

    build();
    items = found;
    renderRail();

    var start = 0;
    if (startSrc) {
      var k = keyOf(startSrc);
      for (var i = 0; i < items.length; i++) {
        if (keyOf(items[i].full) === k || keyOf(items[i].thumb) === k) { start = i; break; }
      }
    }

    lastFocus = document.activeElement;
    lb.setAttribute('data-open', 'true');
    document.body.classList.add('eh-lb-open');
    show(start);
    els.close.focus();
    return true;
  }

  function close() {
    if (!lb) return;
    lb.setAttribute('data-open', 'false');
    document.body.classList.remove('eh-lb-open');
    els.img.removeAttribute('src');
    if (lastFocus && lastFocus.focus) {
      try { lastFocus.focus(); } catch (e) {}
    }
  }

  function isOpen() { return !!lb && lb.getAttribute('data-open') === 'true'; }

  document.addEventListener('keydown', function (e) {
    if (!isOpen()) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
  });

  /* ---- what opens it ----------------------------------------------------
     A capture-phase listener, so the theme's own zoom dialog never gets the
     click on pages where it is still switched on, and there is only ever one
     enlarged view.                                                          */

  var TRIGGERS = [
    'media-gallery slideshow-slide img',
    'media-gallery .product-media img',
    '.shg-product-image-wrapper img',
    '.shg-gallery-scroll-container img'
  ].join(',');

  document.addEventListener('click', function (e) {
    if (isOpen()) return;
    var t = e.target;
    if (!t || t.nodeType !== 1 || !t.closest) return;

    // never hijack a real link, a button, or a video
    if (t.closest('a, button, [role="button"], video, .eh-lb')) return;

    var img = t.closest(TRIGGERS);
    if (!img && t.matches && t.matches(TRIGGERS)) img = t;
    if (!img) return;

    if (open(bestSrc(img))) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  // If the theme's own dialog is still enabled and manages to open anyway,
  // stand down rather than showing two viewers at once.
  document.addEventListener('click', function () {
    setTimeout(function () {
      var native = document.querySelector('dialog.dialog-zoomed-gallery__dialog[open]');
      if (native && isOpen()) close();
    }, 30);
  }, false);

  window.EH_LIGHTBOX = { open: open, close: close, isOpen: isOpen };
})();